import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * F28.1 — Inventario admin per Release Builder: TUTTI i materiali e stratigrafie,
 * sia privati (org-scoped) sia globali (già in vendita), con metadata per
 * decidere se aggiungere alla release in corso.
 */

export type InventoryStatus = 'private' | 'global_current' | 'global_archive';

export interface InventoryMaterial {
  id: string;
  code: string;
  name: string;
  category: string;
  supplier: string | null;
  thickness: string | null;
  list_price: number | null;
  organization_id: string | null;
  organization_name: string | null;
  starter_category: string | null;
  version_year: number | null;
  is_current_version: boolean;
  status: InventoryStatus;
  release_slugs: string[];
}

export interface InventoryStratigraphy {
  id: string;
  name: string;
  system_code: string | null;
  type: string;
  supplier_name: string | null;
  fire_resistance_class: string | null;
  acoustic_performance: number | null;
  total_thickness: number | null;
  organization_id: string | null;
  organization_name: string | null;
  starter_category: string | null;
  version_year: number | null;
  is_current_version: boolean;
  layer_count: number;
  status: InventoryStatus;
  release_slugs: string[];
}

function classify(orgId: string | null, isCurrent: boolean): InventoryStatus {
  if (orgId !== null) return 'private';
  return isCurrent ? 'global_current' : 'global_archive';
}

interface MaterialsFilter {
  brand?: string; // 'fassa', 'saint_gobain', 'knauf' (matcha supplier OR starter_category)
  versionYear?: number;
  category?: string;
  status?: InventoryStatus | 'all';
}

/** Match brand-fuzzy lato JS (PostgREST .or() con % letterali non funziona affidabilmente) */
function matchesBrand(brandSlug: string | undefined, supplier: string | null, starterCategory: string | null): boolean {
  if (!brandSlug) return true;
  const supplierKey = {
    saint_gobain: 'saint',
    knauf: 'knauf',
    fassa: 'fassa',
  }[brandSlug] ?? brandSlug;
  if (supplier && supplier.toLowerCase().includes(supplierKey)) return true;
  if (starterCategory && starterCategory.startsWith(`addon_${brandSlug}_`)) return true;
  return false;
}

export function useAdminAllMaterials(filter: MaterialsFilter = {}) {
  return useQuery<InventoryMaterial[]>({
    queryKey: ['admin', 'all-materials-rpc', filter],
    staleTime: 20_000,
    queryFn: async () => {
      // F28.3: usa RPC SECURITY DEFINER per bypassare RLS/JWT cache problems
      const { data, error } = await supabase.rpc('admin_get_all_materials');
      if (error) throw error;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let rows = (data ?? []).map((row: any) => ({
        id: row.id,
        code: row.code,
        name: row.name,
        category: row.category,
        supplier: row.supplier,
        thickness: row.thickness,
        list_price: row.list_price,
        organization_id: row.organization_id,
        organization_name: row.organization_name ?? null,
        starter_category: row.starter_category,
        version_year: row.version_year,
        is_current_version: row.is_current_version ?? true,
        status: classify(row.organization_id, row.is_current_version ?? true),
        release_slugs: Array.isArray(row.release_slugs) ? row.release_slugs : [],
      })) as InventoryMaterial[];

      if (filter.versionYear !== undefined) rows = rows.filter((r) => r.version_year === filter.versionYear);
      if (filter.category) rows = rows.filter((r) => r.category === filter.category);
      if (filter.brand) rows = rows.filter((r) => matchesBrand(filter.brand, r.supplier, r.starter_category));
      if (filter.status && filter.status !== 'all') rows = rows.filter((r) => r.status === filter.status);
      return rows;
    },
  });
}

interface StratFilter {
  brand?: string;
  versionYear?: number;
  status?: InventoryStatus | 'all';
}

export function useAdminAllStratigraphies(filter: StratFilter = {}) {
  return useQuery<InventoryStratigraphy[]>({
    queryKey: ['admin', 'all-stratigraphies-rpc', filter],
    staleTime: 20_000,
    queryFn: async () => {
      // F28.3: usa RPC SECURITY DEFINER per bypassare qualunque problema RLS/JWT
      const { data, error } = await supabase.rpc('admin_get_all_stratigraphies');
      if (error) throw error;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let rows = (data ?? []).map((row: any) => ({
        id: row.id,
        name: row.name,
        system_code: row.system_code,
        type: row.type,
        supplier_name: row.supplier_name,
        fire_resistance_class: row.fire_resistance_class,
        acoustic_performance: row.acoustic_performance,
        total_thickness: row.total_thickness,
        organization_id: row.organization_id,
        organization_name: row.organization_name ?? null,
        starter_category: row.starter_category,
        version_year: row.version_year,
        is_current_version: row.is_current_version ?? true,
        layer_count: Number(row.layer_count ?? 0),
        status: classify(row.organization_id, row.is_current_version ?? true),
        release_slugs: Array.isArray(row.release_slugs) ? row.release_slugs : [],
      })) as InventoryStratigraphy[];

      if (filter.versionYear !== undefined) rows = rows.filter((r) => r.version_year === filter.versionYear);
      if (filter.brand) rows = rows.filter((r) => matchesBrand(filter.brand, r.supplier_name, r.starter_category));
      if (filter.status && filter.status !== 'all') rows = rows.filter((r) => r.status === filter.status);
      return rows;
    },
  });
}

/** Categorie distinte presenti nel DB (per popolare il filter dropdown) */
export function useMaterialCategories() {
  return useQuery<string[]>({
    queryKey: ['admin', 'material-categories'],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('materials')
        .select('category')
        .not('category', 'is', null)
        .limit(2000);
      if (error) throw error;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const set = new Set((data ?? []).map((r: any) => r.category as string));
      return Array.from(set).sort();
    },
  });
}

/** Anni distinti presenti nel DB (per popolare il filter dropdown) */
export function useDistinctVersionYears() {
  return useQuery<number[]>({
    queryKey: ['admin', 'distinct-years'],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('materials')
        .select('version_year')
        .not('version_year', 'is', null)
        .limit(5000);
      if (error) throw error;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const set = new Set((data ?? []).map((r: any) => Number(r.version_year)).filter((y) => !Number.isNaN(y)));
      return Array.from(set).sort((a, b) => b - a);
    },
  });
}
